import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Info, Handshake, ExternalLink } from "lucide-react";
import TripAutoHero from "../components/TripAutoHero";

/* Advertising & Affiliate Disclosure — canonical /legal/affiliate-disclosure.
   Linked from the site-wide Footer. Plain-English disclosure so travellers
   understand when we earn a commission and when we don't. */
export default function AffiliateDisclosure() {
  useEffect(() => {
    document.title = "Advertising & Affiliate Disclosure | TravelerPulse.pro";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="affiliate-disclosure-page">
      <div className="relative border-b border-zinc-200 overflow-hidden">
        <TripAutoHero query="honest business travel handshake" alt="Affiliate disclosure"
          className="absolute inset-0 w-full h-full" testid="affiliate-disclosure-hero" />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/90 via-primary/70 to-orange-600/55" />
        <div className="relative max-w-4xl mx-auto px-5 lg:px-8 py-14">
          <Link to="/" className="text-xs font-700 text-white/85 inline-flex items-center gap-1 hover:underline" data-testid="affiliate-disclosure-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </Link>
          <p className="mt-3 text-xs font-mono uppercase tracking-widest text-white/85">Legal · Transparency</p>
          <h1 className="mt-2 font-heading font-900 text-4xl sm:text-5xl tracking-tight text-white">
            Advertising &amp; Affiliate Disclosure
          </h1>
          <p className="mt-3 text-base text-white/90 max-w-2xl">
            How TravelerPulse.pro™ makes money — in plain English, so you always know when a link
            or recommendation could earn us a small commission.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 lg:px-8 py-12 space-y-8 article-body text-zinc-800">
        <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground">Last updated: February 2026</p>

        <section>
          <h2 className="font-heading font-900 text-2xl flex items-center gap-2 text-primary">
            <Info className="w-6 h-6" /> The short version
          </h2>
          <p className="mt-3">
            TravelerPulse.pro is a free travel platform. To keep the lights on, we sometimes place
            <strong> affiliate links </strong> and <strong> paid advertisements </strong> alongside our editorial content.
            When you buy something after clicking one of those links, we may earn a small commission
            — <em>at no extra cost to you</em>. Prices, availability and the booking experience are
            identical to what you would see going direct to the merchant.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-900 text-2xl flex items-center gap-2 text-primary">
            <Handshake className="w-6 h-6" /> What that means for you
          </h2>
          <ul className="mt-3 space-y-2 list-disc list-inside">
            <li>Our reviews, guides and rankings are written independently. A merchant paying to advertise cannot buy a better ranking.</li>
            <li>Where a link is a paid partnership we mark it clearly — usually with a small <em>Sponsored</em> or <em>Partner</em> label.</li>
            <li>Community members' reviews and stories are never influenced by commercial partners. They stay separated from official operator information.</li>
            <li>You are always free to book directly with the airline, hotel, cruise line, rail operator or insurance provider. Where an official website exists, we surface it prominently.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading font-900 text-2xl flex items-center gap-2 text-primary">
            <ExternalLink className="w-6 h-6" /> Programs we participate in
          </h2>
          <p className="mt-3">
            We work with a range of travel affiliate networks (booking marketplaces, insurance
            brokers, loyalty programs and gear retailers). The specific programs change over time,
            and we surface partner logos on the relevant category pages. We do not sell your data
            to any of these partners.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-900 text-2xl text-primary">Advertising</h2>
          <p className="mt-3">
            You may see display advertisements or promoted listings on the site. These are labelled
            <em> Advertisement </em> or <em> Sponsored</em>. Advertising revenue never changes the
            factual information on our destination, airport, airline, cruise or rail pages — those
            profiles are sourced from official public records and reader contributions.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-900 text-2xl text-primary">Questions or concerns?</h2>
          <p className="mt-3">
            If you think we've mislabeled a commercial link, or you'd like to know whether a
            particular recommendation is a paid partnership, please reach out via{" "}
            <Link to="/feedback" className="text-primary font-700 underline">Feedback</Link> or{" "}
            <Link to="/contact" className="text-primary font-700 underline">Contact</Link>.
            We'll respond within two business days.
          </p>
        </section>

        <section className="border-t border-zinc-200 pt-6 text-sm text-muted-foreground">
          <p>
            TravelerPulse.pro™ is part of the <a href="https://socialpulse.com" target="_blank" rel="noopener noreferrer" className="underline">SocialPulse Network</a>.
            This disclosure applies to the TravelerPulse.pro, IndianAirports.com and
            AirlinesAirports.com domains, all served from the same platform.
          </p>
        </section>
      </div>
    </div>
  );
}
